import React from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  TypeFilterValue,
  TypeValue,
  RoomsFilterValue,
  PropertyTypeFilterCount,
  RoomsFilterCount,
} from 'ha/types/SearchFilters';
import { useIntl } from 'ha/i18n';

import { useShowStudentAccomodation } from 'ha/modules/PBSA';
import { Option, CommonCheckboxFilter } from './CommonCheckboxFilter';
import { CommonCheckboxTitleWithCount } from './CommonCheckboxTitleWithCount';
import { RoomFilter } from './RoomFilter';

interface Props {
  value: TypeFilterValue;
  onChange: (value: TypeFilterValue) => void;
  roomValue: RoomsFilterValue;
  onChangeRooms: (value: RoomsFilterValue) => void;
  count?: PropertyTypeFilterCount;
  roomsCount?: RoomsFilterCount;
}

const useStyles = makeStyles()(theme => ({
  roomFilter: {
    marginLeft: theme.spacing(4),
  },
}));

const TypeFilter: React.FC<Props> = ({
  value,
  onChange,
  roomValue,
  onChangeRooms,
  count,
  roomsCount,
}) => {
  const { T } = useIntl();
  const { classes } = useStyles();
  const showStudentAccomodationTags = useShowStudentAccomodation();

  let options = React.useMemo<Option[]>(
    () => [
      {
        value: TypeValue.STUDIO,
        title: (
          <CommonCheckboxTitleWithCount
            title={T('Studio')}
            count={count?.studio}
          />
        ),
        testLocator: 'TypeFilter Studio',
      },
      {
        value: TypeValue.APARTMENT,
        title: (
          <CommonCheckboxTitleWithCount
            title={T('Apartment')}
            count={count?.apartment}
          />
        ),
        ...(value.includes(TypeValue.APARTMENT)
          ? {
              childElement: (
                <div className={classes.roomFilter}>
                  <RoomFilter
                    value={roomValue}
                    onChange={onChangeRooms}
                    count={roomsCount}
                  />
                </div>
              ),
            }
          : {}),
        testLocator: 'TypeFilter Apartment',
      },
      {
        value: TypeValue.PRIVATE_ROOM,
        title: (
          <CommonCheckboxTitleWithCount
            title={T('Private Room')}
            count={count?.privateRoom}
          />
        ),
        testLocator: 'TypeFilter PrivateRoom',
      },
      {
        value: TypeValue.SHARED_ROOM,
        title: (
          <CommonCheckboxTitleWithCount
            title={T('Shared Room')}
            count={count?.sharedRoom}
          />
        ),
        testLocator: 'TypeFilter SharedRoom',
      },
    ],
    [
      T,
      count?.studio,
      count?.apartment,
      count?.privateRoom,
      count?.sharedRoom,
      value,
      classes.roomFilter,
      roomValue,
      onChangeRooms,
      roomsCount,
    ],
  );

  if (showStudentAccomodationTags) {
    options = [
      ...options,
      {
        value: TypeValue.STUDENT_HOUSING,
        title: (
          <CommonCheckboxTitleWithCount
            title={T('student_accommodation.search_page.label')}
            count={count?.studentHousing}
          />
        ),
        testLocator: 'TypeFilter StudentHousing',
      },
    ];
  }

  return (
    <CommonCheckboxFilter options={options} value={value} onChange={onChange} />
  );
};

export { TypeFilter };
